import { format, isPast, parseISO } from "date-fns";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Building2,
  Calendar,
  Mail,
  MessageSquare,
  Mic,
  User,
  Video,
} from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../../../lib/utils";
import { Badge } from "../../../../components/ui/badge";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import type { TaskRecord } from "../tasksTypes";
import { BOARD_STATUS_LABELS } from "../tasksTypes";

type TasksListTableProps = {
  tasks: TaskRecord[];
  onTaskClick?: (task: TaskRecord) => void;
};

type SortField = "subject" | "account_name" | "priority" | "board_status" | "due_date" | "created_at" | "account_arr";
type SortOrder = "asc" | "desc";

const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };

const priorityStyles: Record<string, string> = {
  high: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400",
  medium: "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400",
  low: "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400",
};

const sourceIcons: Record<string, typeof Calendar> = {
  meetings: Video,
  emails: Mail,
  notes: MessageSquare,
  recordings: Mic,
};

const formatRevenue = (value?: number) => {
  if (value == null) return null;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const columns: Array<{ key: SortField; label: string; sortable: boolean }> = [
  { key: "subject", label: "Subject", sortable: true },
  { key: "account_name", label: "Account", sortable: true },
  { key: "account_arr", label: "ARR / MRR", sortable: true },
  { key: "priority", label: "Priority", sortable: true },
  { key: "board_status", label: "Status", sortable: true },
  { key: "due_date", label: "Due Date", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
];

const TaskRow = ({
  task,
  onTaskClick,
}: {
  task: TaskRecord;
  onTaskClick?: (task: TaskRecord) => void;
}) => {
  const companyDomain = task.account_domain || (task.account_name ? getDomainFromCompany(task.account_name) : "");
  const isOverdue =
    task.due_date && task.board_status !== "done" && isPast(parseISO(task.due_date));
  const SourceIcon = sourceIcons[task.source_type?.toLowerCase() || ""] || null;
  const priorityClass = priorityStyles[task.priority?.toLowerCase() || ""] || "";

  return (
    <div
      className="grid grid-cols-[1fr_160px_100px_90px_110px_100px_100px] items-center gap-4 border-b border-border px-6 py-3 transition-colors hover:bg-muted cursor-pointer"
      onClick={() => onTaskClick?.(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onTaskClick?.(task); }}
    >
      {/* Subject */}
      <div className="min-w-0 flex items-center gap-2">
        {SourceIcon && <SourceIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground">
            {task.subject || "Untitled task"}
          </span>
          {task.assigned_to && (
            <span className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              {task.assigned_to}
            </span>
          )}
        </div>
      </div>

      {/* Account */}
      <div className="flex min-w-0 items-center gap-2">
        {task.account_name ? (
          <>
            <CompanyLogo
              domain={companyDomain}
              name={task.account_name}
              className="h-5 w-5 shrink-0"
            />
            <span className="truncate text-sm text-foreground">{task.account_name}</span>
          </>
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4 shrink-0" />
            --
          </span>
        )}
      </div>

      {/* ARR / MRR */}
      <div>
        {task.account_arr != null || task.account_mrr != null ? (
          <span className="text-sm text-foreground">
            {formatRevenue(task.account_arr) || formatRevenue(task.account_mrr) || "--"}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">--</span>
        )}
      </div>

      {/* Priority */}
      <div>
        {task.priority ? (
          <Badge
            variant="outline"
            className={cn("text-[11px] font-medium", priorityClass)}
          >
            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">--</span>
        )}
      </div>

      {/* Board Status */}
      <div>
        <Badge variant="outline" className="text-[11px] font-normal border-border bg-muted text-muted-foreground">
          {BOARD_STATUS_LABELS[task.board_status] || task.board_status}
        </Badge>
      </div>

      {/* Due Date */}
      <div>
        {task.due_date ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 text-sm",
              isOverdue
                ? "font-medium text-red-600 dark:text-red-400"
                : "text-foreground",
            )}
          >
            {format(parseISO(task.due_date), "MMM d")}
            {isOverdue && <AlertCircle className="h-3.5 w-3.5" />}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">--</span>
        )}
      </div>

      {/* Created */}
      <div>
        {task.created_at ? (
          <span className="text-sm text-muted-foreground">
            {format(parseISO(task.created_at), "MMM d")}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">--</span>
        )}
      </div>
    </div>
  );
};

const TasksListTable = ({ tasks, onTaskClick }: TasksListTableProps) => {
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      let cmp = 0;

      switch (sortField) {
        case "subject":
          cmp = (a.subject || "").localeCompare(b.subject || "");
          break;
        case "account_name":
          cmp = (a.account_name || "").localeCompare(b.account_name || "");
          break;
        case "account_arr":
          cmp = (a.account_arr || a.account_mrr || 0) - (b.account_arr || b.account_mrr || 0);
          break;
        case "priority":
          cmp = (priorityOrder[a.priority?.toLowerCase() || ""] ?? 3) -
                (priorityOrder[b.priority?.toLowerCase() || ""] ?? 3);
          break;
        case "board_status":
          cmp = (a.board_status || "").localeCompare(b.board_status || "");
          break;
        case "due_date":
          cmp = (a.due_date || "").localeCompare(b.due_date || "");
          break;
        case "created_at":
          cmp = (a.created_at || "").localeCompare(b.created_at || "");
          break;
      }

      return sortOrder === "asc" ? cmp : -cmp;
    });
  }, [tasks, sortField, sortOrder]);

  const SortIcon = sortOrder === "asc" ? ArrowUp : ArrowDown;

  return (
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 grid grid-cols-[1fr_160px_100px_90px_110px_100px_100px] items-center gap-4 border-b border-border bg-muted px-6 py-2">
        {columns.map((col) => (
          <button
            key={col.key}
            type="button"
            onClick={() => col.sortable && handleSort(col.key)}
            className={cn(
              "flex items-center gap-1 text-xs font-medium text-muted-foreground",
              col.sortable && "cursor-pointer hover:text-foreground",
            )}
          >
            {col.label}
            {sortField === col.key && <SortIcon className="h-3 w-3" />}
          </button>
        ))}
      </div>

      {/* Rows */}
      {sortedTasks.map((task) => (
        <TaskRow key={task.id} task={task} onTaskClick={onTaskClick} />
      ))}
    </div>
  );
};

export default TasksListTable;
